import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Menu, X, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Global TopNav — riutilizzato da Hub, landing prodotto e pagine marketing.
 *
 * Comportamento:
 * - Trasparente fino a 100px di scroll → glass blur dopo
 * - Stato guest: Accedi + "Entra nell'Ecosistema"
 * - Stato auth: avatar + "Apri Workspace"
 * - Mobile: menu hamburger fullscreen
 *
 * Variants:
 *   variant="hub"       (dark transparent, per cinematic landing)
 *   variant="product"   (dark solid, per landing prodotto)
 *
 * Props:
 *   menu       Array<{label, href, anchor?}>  link centrali
 *   sticky     bool   default true (fixed top)
 */

const DEFAULT_MENU = [
    { label: "Aria", href: "/aria" },
    { label: "Ecosistema", anchor: "hub-scene-network" },
    { label: "Prodotti", anchor: "hub-scene-moduli" },
    { label: "Contatti", anchor: "hub-scene-contatti" },
];

const SOFT_EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

export default function TopNav({ menu = DEFAULT_MENU, variant = "hub", sticky = true }) {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [openMobile, setOpenMobile] = useState(false);
    const isLogged = !!user && typeof user === "object";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 80);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpenMobile(false);
    }, [location.pathname]);

    const handleMenuClick = (item, e) => {
        if (item.action === "aria") {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent("open-aria"));
            return;
        }
        if (item.anchor) {
            e.preventDefault();
            // se siamo sulla home, scroll alla sezione; altrimenti vai alla home e scrolla dopo
            if (location.pathname !== "/") {
                navigate("/", { state: { scrollTo: item.anchor } });
                return;
            }
            const el = document.querySelector(`[data-testid='${item.anchor}']`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    // background dinamico
    const bgStyle = variant === "hub"
        ? {
            background: scrolled ? "rgba(5, 5, 15, 0.65)" : "transparent",
            backdropFilter: scrolled ? "blur(18px) saturate(140%)" : "none",
            WebkitBackdropFilter: scrolled ? "blur(18px) saturate(140%)" : "none",
            borderBottom: scrolled ? "1px solid rgba(255,255,255,0.07)" : "1px solid transparent",
            transition: `all 600ms ${SOFT_EASE}`,
        }
        : {
            background: "rgba(10, 16, 36, 0.86)",
            backdropFilter: "blur(14px) saturate(140%)",
            WebkitBackdropFilter: "blur(14px) saturate(140%)",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
        };

    return (
        <header
            data-testid="topnav"
            className={`${sticky ? "fixed" : "relative"} top-0 left-0 right-0 z-50 px-5 md:px-10 py-3.5 md:py-4`}
            style={bgStyle}
        >
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
                {/* Logo */}
                <Link
                    to="/"
                    onClick={() => {
                        if (typeof window !== "undefined" && window.location.pathname === "/") {
                            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                        }
                    }}
                    className="flex items-center group"
                    data-testid="topnav-logo"
                    aria-label="TotalKlick home"
                >
                    <img
                        src="/brand/totalklick-logo.svg"
                        alt="TotalKlick"
                        className="h-6 md:h-7 w-auto transition-transform group-hover:scale-[1.03]"
                        style={{ filter: "drop-shadow(0 0 18px rgba(79,139,255,0.45))" }}
                    />
                </Link>

                {/* Center menu desktop */}
                <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
                    {menu.map((item, i) => (
                        <a
                            key={i}
                            href={item.href || `#${item.anchor || ""}`}
                            onClick={(e) => handleMenuClick(item, e)}
                            className="px-3.5 py-2 rounded-full text-[13px] text-white/75 hover:text-white hover:bg-white/5 transition"
                            style={{ fontFamily: "Geist, sans-serif", letterSpacing: "0.01em" }}
                            data-testid={`topnav-menu-${(item.label || "").toLowerCase()}`}
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                {/* Right CTAs */}
                <div className="hidden md:flex items-center gap-2">
                    {isLogged ? (
                        <>
                            <UserAvatar user={user} />
                            <Link
                                to="/app"
                                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-white text-[13px] transition hover:scale-[1.03]"
                                style={{
                                    background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                                    fontFamily: "Geist, sans-serif",
                                    letterSpacing: "0.02em",
                                    boxShadow: "0 10px 30px -8px rgba(79,139,255,0.55)",
                                }}
                                data-testid="topnav-open-workspace"
                            >
                                Apri Workspace <ArrowRight size={13} />
                            </Link>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                className="px-4 py-2 rounded-full text-[13px] text-white/85 hover:text-white border border-white/15 hover:bg-white/5 transition"
                                style={{ fontFamily: "Geist, sans-serif", letterSpacing: "0.02em" }}
                                data-testid="topnav-login"
                            >
                                Accedi
                            </Link>
                            <Link
                                to="/signup"
                                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-white text-[13px] transition hover:scale-[1.03]"
                                style={{
                                    background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                                    fontFamily: "Geist, sans-serif",
                                    letterSpacing: "0.02em",
                                    boxShadow: "0 10px 30px -8px rgba(79,139,255,0.55)",
                                }}
                                data-testid="topnav-signup"
                            >
                                Entra nell&apos;Ecosistema <ArrowRight size={13} />
                            </Link>
                        </>
                    )}
                </div>

                {/* Mobile hamburger */}
                <button
                    type="button"
                    onClick={() => setOpenMobile(true)}
                    className="md:hidden text-white/80 hover:text-white"
                    aria-label="Apri menu"
                    data-testid="topnav-mobile-open"
                >
                    <Menu size={22} />
                </button>
            </div>

            {/* Mobile menu fullscreen */}
            {openMobile && (
                <div
                    className="md:hidden fixed inset-0 z-[60] flex flex-col"
                    style={{ background: "rgba(5,5,15,0.96)", backdropFilter: "blur(20px)" }}
                    data-testid="topnav-mobile-panel"
                >
                    <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
                        <span className="text-white text-[15px] font-medium" style={{ fontFamily: "Geist, sans-serif" }}>
                            TotalKlick
                        </span>
                        <button
                            type="button"
                            onClick={() => setOpenMobile(false)}
                            className="text-white/80 hover:text-white"
                            aria-label="Chiudi menu"
                            data-testid="topnav-mobile-close"
                        >
                            <X size={22} />
                        </button>
                    </div>
                    <nav className="flex-1 flex flex-col gap-1 px-5 py-6">
                        {menu.map((item, i) => (
                            <a
                                key={i}
                                href={item.href || `#${item.anchor || ""}`}
                                onClick={(e) => handleMenuClick(item, e)}
                                className="block py-3 text-[19px] text-white/85 hover:text-white border-b border-white/5"
                                style={{ fontFamily: "Geist, sans-serif", letterSpacing: "-0.01em" }}
                                data-testid={`topnav-mobile-${(item.label || "").toLowerCase()}`}
                            >
                                {item.label}
                            </a>
                        ))}
                    </nav>
                    <div className="px-5 pb-8 pt-4 flex flex-col gap-2.5">
                        {isLogged ? (
                            <Link
                                to="/app"
                                className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-white text-[15px]"
                                style={{
                                    background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                                    fontFamily: "Geist, sans-serif",
                                    letterSpacing: "0.02em",
                                }}
                                data-testid="topnav-mobile-workspace"
                            >
                                Apri Workspace <ArrowRight size={15} />
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    className="w-full text-center py-3.5 rounded-full text-[15px] text-white border border-white/15"
                                    style={{ fontFamily: "Geist, sans-serif", letterSpacing: "0.02em" }}
                                    data-testid="topnav-mobile-login"
                                >
                                    Accedi
                                </Link>
                                <Link
                                    to="/signup"
                                    className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-white text-[15px]"
                                    style={{
                                        background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                                        fontFamily: "Geist, sans-serif",
                                        letterSpacing: "0.02em",
                                    }}
                                    data-testid="topnav-mobile-signup"
                                >
                                    Entra nell&apos;Ecosistema <ArrowRight size={15} />
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}

function UserAvatar({ user }) {
    const initials = (user?.first_name || user?.email || "?")
        .split(/[\s@.]/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() || "")
        .join("");
    return (
        <Link
            to="/app"
            className="w-9 h-9 grid place-items-center rounded-full border border-white/15 text-white text-[12px] font-bold hover:bg-white/5 transition"
            style={{
                background: "linear-gradient(135deg, rgba(79,139,255,0.25), rgba(43,90,255,0.15))",
                fontFamily: "Geist, sans-serif",
            }}
            title={user?.email || "Workspace"}
            data-testid="topnav-avatar"
        >
            {initials || <Sparkles size={14} />}
        </Link>
    );
}
